import { queryOptions } from "@tanstack/react-query";
import { fetchOccPageData } from "./pageDataClient";
import { pageDataKeys } from "./pageDataKeys";
import type { PageInitialDataResult } from "./pageInitialDataTypes";
import type { SearchAjaxPageInitialData, SearchPageInitialData } from "./searchPageInitialData";

export type SearchPageQueryInput =
  | {
      variant: "text";
      text: string;
      techValues: string | null;
      assistantValues: string | null;
      isSelectionAssistant: string;
    }
  | {
      variant: "refine";
      q: string;
      page: string;
      show: string;
      sort: string | null;
      text: string;
      techValues: string | null;
      assistantValues: string | null;
      isSelectionAssistant: string;
    };

/**
 * Page-data query for the text-search variant
 * ({@code GET /occ/v2/{baseSiteId}/schaeffler/page-data/search}, no {@code q} param).
 * The route loader fetches this for shell metadata (templateUid, breadcrumbs,
 * serverMessages) only; the {@code SearchPage} component loads its own results via
 * RTK Query.
 */
export const searchPageQueryOptions = (input: SearchPageQueryInput) =>
  queryOptions({
    queryKey: pageDataKeys.pageWithInputs("search", input),
    queryFn: () => fetchOccPageData<PageInitialDataResult<SearchPageInitialData>>(buildSearchPageDataUrl(input)),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: 1,
  });

function buildSearchPageDataUrl(input: SearchPageQueryInput): string {
  const params = new URLSearchParams();
  if (input.variant === "refine") {
    params.set("q", input.q);
    params.set("page", input.page);
    params.set("show", input.show);
    if (input.sort !== null) {
      params.set("sort", input.sort);
    }
  }
  if (input.text) {
    params.set("text", input.text);
  }
  if (input.techValues !== null) {
    params.set("techValues", input.techValues);
  }
  if (input.assistantValues !== null) {
    params.set(input.variant === "text" ? "asssistantValues" : "assistantValues", input.assistantValues);
  }
  if (input.isSelectionAssistant) {
    params.set("isSelectionAssistant", input.isSelectionAssistant);
  }
  const query = params.toString();
  return `/page-data/search${query ? `?${query}` : ""}`;
}

/**
 * Page-data query for the ajax/infinite-scroll variant
 * ({@code GET /occ/v2/{baseSiteId}/schaeffler/page-data/search/searchpage}). The
 * {@code /search/searchpage} Spring route renders the same React shell, so its
 * loader fetches this endpoint for metadata.
 */
export const searchAjaxPageQueryOptions = () =>
  queryOptions({
    queryKey: pageDataKeys.page("search-ajax"),
    queryFn: () => fetchOccPageData<PageInitialDataResult<SearchAjaxPageInitialData>>("/page-data/search/searchpage"),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: 1,
  });
